import { apiGetAuth } from '@/utils/api'
import { API_COMPANY, API_EMPLOYEE, API_CLAIM, API_LPD } from '@/utils/api-url'
import {
  notificationDanger
} from '@/utils/notification'

const BASE_URL = `${process.env.VUE_APP_API_URL}`

const state = {
  total_company: { data: [] },
  total_company_counts: {},
  total_employee_counts: {},
  total_change_counts: {},
  total_claim_counts: {},
  company_employee_counts: {},
  company_change_counts: {},
  company_claim_counts: {},
  latest_upc_date: null
}

const mutations = {
  SET_TOTAL_COMPANY: (state, payload) => {
    state.total_company = payload
  },
  SET_TOTAL_COMPANY_COUNTS: (state, payload) => {
    state.total_company_counts = payload
  },
  SET_TOTAL_EMPLOYEE_COUNTS: (state, payload) => {
    state.total_employee_counts = payload
  },
  SET_TOTAL_CHANGE_COUNTS: (state, payload) => {
    state.total_change_counts = payload
  },
  SET_TOTAL_CLAIM_COUNTS: (state, payload) => {
    state.total_claim_counts = payload
  },
  SET_COMPANY_EMPLOYEE_COUNTS: (state, payload) => {
    state.company_employee_counts = payload
  },
  SET_COMPANY_CHANGE_COUNTS: (state, payload) => {
    state.company_change_counts = payload
  },
  SET_COMPANY_CLAIM_COUNTS: (state, payload) => {
    state.company_claim_counts = payload
  },
  SET_LATEST_UPC_DATE: (state, payload) => {
    state.latest_upc_date = payload
  }
}

const actions = {
  async getTotalCompany(context, params) {
    try {
      const response = await apiGetAuth(API_COMPANY.LIST, params)
      context.commit('SET_TOTAL_COMPANY', response?.data)
    } catch (err) {
      notificationDanger(err)
    }
  },
  async getTotalCompanyCounts(context) {
    try {
      const statuses = ['active', 'inactive']
      const results = await Promise.all(
        statuses.map(s =>
          apiGetAuth(API_COMPANY.LIST, { status: s, limit: 1 })
        )
      )
      const counts = {}
      statuses.forEach((s, i) => {
        counts[s] = results[i]?.data?.other?.total || 0
      })
      context.commit('SET_TOTAL_COMPANY_COUNTS', counts)
    } catch (err) {
      notificationDanger(err)
    }
  },
  async getTotalEmployeeCounts(context) {
    try {
      const statuses = ['new', 'active', 'inactive', 'freeze']
      const results = await Promise.all(
        statuses.map(s =>
          apiGetAuth(API_EMPLOYEE.LIST, { status: s, limit: 1 })
        )
      )
      const counts = {}
      statuses.forEach((s, i) => {
        counts[s] = results[i]?.data?.other?.total || 0
      })
      context.commit('SET_TOTAL_EMPLOYEE_COUNTS', counts)
    } catch (err) {
      notificationDanger(err)
    }
  },
  async getTotalChangeCounts(context) {
    try {
      const statuses = ['process', 'reject', 'approve']
      const results = await Promise.all(
        statuses.map(s =>
          apiGetAuth(`${BASE_URL}/v1/change`, { status: s, limit: 1 })
        )
      )
      const counts = {}
      statuses.forEach((s, i) => {
        counts[s] = results[i]?.data?.other?.total || 0
      })
      context.commit('SET_TOTAL_CHANGE_COUNTS', counts)
    } catch (err) {
      notificationDanger(err)
    }
  },
  async getTotalClaimCounts(context) {
    try {
      const response = await apiGetAuth(API_CLAIM.LIST_DASHBOARD, {})
      const counts = {}
      const list = response?.data?.data || []
      list.forEach(item => {
        if (item?.category) counts[item.category.toLowerCase()] = item.total || 0
      })
      context.commit('SET_TOTAL_CLAIM_COUNTS', counts)
    } catch (err) {
      notificationDanger(err)
    }
  },
  async getCompanyEmployeeCounts(context, { companyId }) {
    try {
      const statuses = ['new', 'active', 'inactive', 'freeze']
      const results = await Promise.all(
        statuses.map(s =>
          apiGetAuth(API_EMPLOYEE.LIST, { company_id: companyId, status: s, limit: 1 })
        )
      )
      const counts = {}
      statuses.forEach((s, i) => {
        counts[s] = results[i]?.data?.other?.total || 0
      })
      context.commit('SET_COMPANY_EMPLOYEE_COUNTS', counts)
    } catch (err) {
      notificationDanger(err)
    }
  },
  async getCompanyChangeCounts(context, { companyId }) {
    try {
      const statuses = ['process', 'reject']
      const results = await Promise.all(
        statuses.map(s =>
          apiGetAuth(`${BASE_URL}/v1/change`, { company_id: companyId, status: s, limit: 1 })
        )
      )
      const counts = {}
      statuses.forEach((s, i) => {
        counts[s] = results[i]?.data?.other?.total || 0
      })
      context.commit('SET_COMPANY_CHANGE_COUNTS', counts)
    } catch (err) {
      notificationDanger(err)
    }
  },
  async getCompanyClaimCounts(context, { companyId }) {
    try {
      const response = await apiGetAuth(API_CLAIM.LIST_DASHBOARD, { company_id: companyId })
      const counts = {}
      const list = response?.data?.data || []
      list.forEach(item => {
        if (item?.category) counts[item.category.toLowerCase()] = item.total || 0
      })
      context.commit('SET_COMPANY_CLAIM_COUNTS', counts)
    } catch (err) {
      notificationDanger(err)
    }
  },
  async getLatestUpcDate(context) {
    try {
      const response = await apiGetAuth(API_LPD.LATEST_UPC_DATE, {})
      context.commit('SET_LATEST_UPC_DATE', response?.data?.data?.latest_upc_date || null)
    } catch (err) {
      notificationDanger(err)
    }
  }
}

const getters = {
  get_total_company: state => {
    return state.total_company
  },
  get_total_company_counts: state => {
    return state.total_company_counts
  },
  get_total_employee_counts: state => {
    return state.total_employee_counts
  },
  get_total_change_counts: state => {
    return state.total_change_counts
  },
  get_total_claim_counts: state => {
    return state.total_claim_counts
  },
  get_company_employee_counts: state => {
    return state.company_employee_counts
  },
  get_company_change_counts: state => {
    return state.company_change_counts
  },
  get_company_claim_counts: state => {
    return state.company_claim_counts
  },
  get_latest_upc_date: state => {
    return state.latest_upc_date
  }
}

const namespaced = true
export default {
  namespaced,
  state,
  mutations,
  actions,
  getters
}
